import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useState } from 'react';
import { studyTypePrices, type StudyTypePrice, type StudyTypePriceInput } from '@/api/endpoints';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { formatMoney } from '@/lib/money';

/** Lista typów opracowań z dozwolonymi wariantami (pochodzi z backendowego StudyType.variants()). */
const STUDY_TYPES: { code: string; name: string; abbr: string; variants: string[] }[] = [
  { code: 'PRG_1A',          name: 'Projekt robót geol. — wykonanie otworu', abbr: 'PRG',       variants: ['SIMPLE', 'COMPLEX', 'VERY_COMPLEX'] },
  { code: 'PRG_1B',          name: 'Projekt robót geol. — likwidacja',       abbr: 'PRG',       variants: ['SIMPLE', 'COMPLEX', 'VERY_COMPLEX'] },
  { code: 'PRG_1C',          name: 'Projekt robót geol. — rekonstrukcja',    abbr: 'PRG',       variants: ['SIMPLE', 'COMPLEX', 'VERY_COMPLEX'] },
  { code: 'KIP_2',           name: 'Karta informacyjna przedsięwzięcia',     abbr: 'KIP',       variants: ['SIMPLE', 'COMPLEX', 'VERY_COMPLEX'] },
  { code: 'OP_WOD_3A',       name: 'Operat w-p — urządzenie wodne',          abbr: 'Op-wod',    variants: ['SIMPLE', 'COMPLEX', 'VERY_COMPLEX', 'VC_PUMPING', 'VC_MONITORING'] },
  { code: 'OP_WOD_3B',       name: 'Operat w-p — pobór wód',                 abbr: 'Op-wod',    variants: ['SIMPLE', 'COMPLEX', 'VERY_COMPLEX', 'VC_PUMPING', 'VC_MONITORING'] },
  { code: 'OP_WOD_3C',       name: 'Operat w-p — odprowadzenie',             abbr: 'Op-wod',    variants: ['SIMPLE', 'COMPLEX', 'VERY_COMPLEX', 'VC_PUMPING', 'VC_MONITORING'] },
  { code: 'DOK_HYDRO_4A',    name: 'Dok. hydrogeol. — zasoby eksploatacyjne',abbr: 'Dok-hydro', variants: ['SIMPLE', 'COMPLEX', 'VERY_COMPLEX', 'VC_ZONES'] },
  { code: 'DOK_HYDRO_4B',    name: 'Dok. hydrogeol. — wydajność otworów',    abbr: 'Dok-hydro', variants: ['SIMPLE', 'COMPLEX', 'VERY_COMPLEX', 'VC_ZONES'] },
  { code: 'DOK_HYDRO_4C',    name: 'Dok. hydrogeol. — strefy ochronne',      abbr: 'Dok-hydro', variants: ['SIMPLE', 'COMPLEX', 'VERY_COMPLEX', 'VC_ZONES'] },
  { code: 'DOK_LIKW_5',      name: 'Dok. geol. z likwidacji otworu',         abbr: 'Dok-likw',  variants: ['SIMPLE', 'COMPLEX', 'VERY_COMPLEX'] },
  { code: 'ANALIZA_RYZYKA_6',name: 'Analiza ryzyka',                         abbr: 'An-ryz',    variants: ['SIMPLE', 'COMPLEX'] },
  { code: 'PLAN_BEZP_7',     name: 'Plan bezpieczeństwa wody (WSP)',         abbr: 'Plan-bezp', variants: ['SIMPLE', 'COMPLEX'] },
  { code: 'PLAN_RUCHU_8',    name: 'Plan Ruchu',                             abbr: 'Plan-ruchu',variants: ['SIMPLE', 'COMPLEX'] },
];

/** Cennik per (StudyType, variant). Wartości pobierane domyślnie do pozycji oferty (edytowalne). */
export function StudyTypePriceAdmin() {
  const [rows, setRows] = useState<StudyTypePrice[] | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState<StudyTypePrice | null>(null);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reload = () => {
    setErr(null);
    setRows(null);
    studyTypePrices.list(includeInactive).then(setRows).catch(e => setErr(e?.message ?? String(e)));
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [includeInactive]);

  const onDeactivate = async (p: StudyTypePrice) => {
    if (!confirm(`Dezaktywować cennik „${p.studyType} / ${p.variant}"?`)) return;
    try {
      await studyTypePrices.delete(p.id);
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  // Grupowanie po typie opracowania dla czytelności
  const grouped = (rows ?? []).reduce<Record<string, StudyTypePrice[]>>((acc, p) => {
    (acc[p.studyType] = acc[p.studyType] ?? []).push(p);
    return acc;
  }, {});

  return (
    <div className="max-w-[1300px]">
      <header className="flex items-end justify-between mb-5 gap-4 flex-wrap">
        <div>
          <h1>Cennik opracowań</h1>
          <div className="text-ink-400 text-[13px]">
            Ceny domyślne per (typ opracowania, wariant). Używane do prefilla pozycji w ofertach —
            cena pozostaje edytowalna w ofercie.
          </div>
        </div>
        <div className="flex gap-2">
          <label className="text-[12px] text-ink-700 flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={includeInactive} onChange={e => setIncludeInactive(e.target.checked)}/>
            Pokaż dezaktywowane
          </label>
          <button onClick={() => setCreating(true)} className="btn btn-primary btn-sm">
            <Icon name="plus" className="w-3.5 h-3.5"/> Nowa pozycja
          </button>
        </div>
      </header>

      {err && <div className="text-red-500 mb-3 text-[12px]">Błąd: {err}</div>}

      {!rows && <div className="text-ink-400 p-4">Ładowanie…</div>}
      {rows && Object.keys(grouped).length === 0 && (
        <div className="card"><div className="card-body text-ink-400">Brak pozycji w cenniku. Dodaj pierwszą.</div></div>
      )}
      {rows && Object.entries(grouped).map(([studyType, items]) => {
        const meta = STUDY_TYPES.find(t => t.code === studyType);
        return (
          <div key={studyType} className="card mb-3">
            <div className="card-head">
              <span className="pill pill-earth">{meta?.abbr ?? studyType}</span>
              <span className="card-title ml-2">{meta?.name ?? studyType}</span>
              <span className="ml-auto text-[11px] text-ink-400 font-mono">{items.length} pozycji</span>
            </div>
            <div className="card-body !p-0">
              <table className="table-hw">
                <thead>
                  <tr>
                    <th className="w-32">Wariant</th>
                    <th>Opis</th>
                    <th className="w-20">Jedn.</th>
                    <th className="w-20">Domyślna ilość</th>
                    <th className="w-32 text-right">Cena netto</th>
                    <th className="w-20">Sort</th>
                    <th className="w-24">Status</th>
                    <th className="w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(p => (
                    <tr key={p.id} className={p.active ? '' : 'opacity-50'}>
                      <td className="font-mono text-[11px] text-ink-800">{p.variant}</td>
                      <td className="text-[12px] text-ink-700 max-w-[420px]">{p.description ?? '—'}</td>
                      <td className="font-mono text-[11px]">{p.unit}</td>
                      <td className="font-mono text-[11px] text-right">{p.defaultQuantity}</td>
                      <td className="font-mono text-[12px] text-right tabular-nums">
                        {formatMoney(p.unitPriceNet)} zł
                      </td>
                      <td className="font-mono text-[11px] text-ink-500">{p.sortOrder}</td>
                      <td>{p.active
                        ? <span className="pill pill-green">aktywna</span>
                        : <span className="pill pill-amber">dezakt.</span>}</td>
                      <td className="text-right whitespace-nowrap">
                        <button onClick={() => setEditing(p)} className="text-water-600 hover:text-water-800 p-1" title="Edytuj">
                          <Icon name="edit" className="w-3.5 h-3.5"/>
                        </button>
                        {p.active && (
                          <button onClick={() => onDeactivate(p)} className="text-red-500 hover:text-red-700 p-1 ml-1" title="Dezaktywuj">
                            <Icon name="x" className="w-3.5 h-3.5"/>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {(creating || editing) && (
        <PriceEditModal
          initial={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); reload(); }}
        />
      )}
    </div>
  );
}

function PriceEditModal({ initial, onClose, onSaved }: {
  initial: StudyTypePrice | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = initial != null;
  const [form, setForm] = useState<StudyTypePriceInput>({
    studyType: initial?.studyType ?? STUDY_TYPES[0].code,
    variant: initial?.variant ?? STUDY_TYPES[0].variants[0],
    unit: initial?.unit ?? 'kpl.',
    defaultQuantity: initial?.defaultQuantity ?? 1,
    unitPriceNet: initial?.unitPriceNet ?? 0,
    description: initial?.description ?? '',
    sortOrder: initial?.sortOrder ?? 100,
    active: initial?.active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const allowedVariants = STUDY_TYPES.find(t => t.code === form.studyType)?.variants ?? [];

  const save = async () => {
    if (!form.studyType) { setErr('Typ opracowania jest wymagany'); return; }
    if (!form.variant) { setErr('Wariant jest wymagany'); return; }
    if (!form.unitPriceNet || Number(form.unitPriceNet) <= 0) {
      setErr('Cena netto musi być dodatnia');
      return;
    }
    setBusy(true); setErr(null);
    try {
      if (isEdit && initial) await studyTypePrices.update(initial.id, form);
      else await studyTypePrices.create(form);
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: form, onClose, busy,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 overflow-auto" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <div className="bg-white rounded-lg shadow-2xl max-w-[640px] w-full mt-8" onClick={e => e.stopPropagation()}>
        <div className="border-b border-ink-100 px-5 py-3 flex items-center gap-3">
          <h2 className="text-[16px] font-semibold m-0">
            {isEdit ? `Edycja cennika: ${initial?.studyType} / ${initial?.variant}` : 'Nowa pozycja cennika'}
          </h2>
          <button onClick={requestClose} className="ml-auto text-ink-400 hover:text-ink-700"><Icon name="x"/></button>
        </div>
        <div className="p-5 space-y-3 max-h-[80vh] overflow-y-auto">
          {err && <div className="text-red-500 text-[12px]">{err}</div>}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Typ opracowania*">
              <SearchableSelect
                value={form.studyType}
                disabled={isEdit}
                onChange={v => {
                  const t = STUDY_TYPES.find(x => x.code === v);
                  setForm({ ...form, studyType: v, variant: t?.variants[0] ?? '' });
                }}>
                {STUDY_TYPES.map(t => <option key={t.code} value={t.code}>{t.abbr}-{t.code} — {t.name}</option>)}
              </SearchableSelect>
            </Field>
            <Field label="Wariant*">
              <SearchableSelect value={form.variant} disabled={isEdit}
                      onChange={v => setForm({ ...form, variant: v })}>
                {allowedVariants.map(v => <option key={v} value={v}>{v}</option>)}
              </SearchableSelect>
            </Field>
          </div>

          <Field label="Opis (kopiowany do opisu pozycji oferty)">
            <textarea value={form.description ?? ''}
                      onChange={e => setForm({ ...form, description: e.target.value })}
                      placeholder="np. Operat wodnoprawny — pobór wód, wariant prosty"
                      rows={2} className="input min-h-[50px]"/>
          </Field>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Jednostka">
              <input value={form.unit ?? 'kpl.'}
                     onChange={e => setForm({ ...form, unit: e.target.value })}
                     placeholder="kpl., szt., mb."
                     className="input font-mono"/>
            </Field>
            <Field label="Domyślna ilość">
              <input type="number" step="0.001" value={form.defaultQuantity ?? 1}
                     onChange={e => setForm({ ...form, defaultQuantity: Number(e.target.value) })}
                     className="input font-mono"/>
            </Field>
            <Field label="Cena netto*">
              <input type="number" step="0.01" min="0.01" value={form.unitPriceNet}
                     onChange={e => setForm({ ...form, unitPriceNet: Number(e.target.value) })}
                     className="input font-mono"/>
            </Field>
          </div>

          <div className="flex items-center gap-5">
            <Field label="Kolejność sortowania">
              <input type="number" value={form.sortOrder ?? 100}
                     onChange={e => setForm({ ...form, sortOrder: Number(e.target.value) })}
                     className="input font-mono w-32"/>
            </Field>
            {isEdit && (
              <label className="flex items-center gap-2 text-[12px] text-ink-700 cursor-pointer mt-5">
                <input type="checkbox" checked={form.active ?? true}
                       onChange={e => setForm({ ...form, active: e.target.checked })}/>
                Aktywna (widoczna w pickerze ofert)
              </label>
            )}
          </div>
        </div>
        <div className="border-t border-ink-100 px-5 py-3 flex justify-end gap-2">
          <button onClick={requestClose} disabled={busy} className="btn btn-secondary btn-sm">Anuluj</button>
          <button onClick={save} disabled={busy} className="btn btn-primary btn-sm">
            {busy ? 'Zapisywanie…' : (isEdit ? 'Zapisz zmiany' : 'Dodaj pozycję')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-500">{label}</span>
      {children}
    </div>
  );
}
